import React from "react";
import { ResponsiveChartContainer } from "@mui/x-charts/ResponsiveChartContainer";
import { BarPlot } from "@mui/x-charts/BarChart";
import { LinePlot, AreaPlot, MarkPlot } from "@mui/x-charts/LineChart";
import { ChartsXAxis } from "@mui/x-charts/ChartsXAxis";
import { ChartsYAxis } from "@mui/x-charts/ChartsYAxis";
import {
  ChartsAxisContentProps,
  ChartsTooltip,
} from "@mui/x-charts/ChartsTooltip";
import { ChartsReferenceLine } from "@mui/x-charts/ChartsReferenceLine";
import { Box, Divider, Paper, Typography } from "@mui/material";
import NumberFormatterUtils from "../../../../utils/numberFormatterUtils";
import { useTranslation } from "react-i18next";
import { common } from "../../../../i18n";
import {
  GoalDashboardBaseline,
  GoalDashboardBaseYear,
  GoalItem,
} from "../../../../models/GoalSettingDashboard";
import { axisClasses, AxisConfig, chartsGridClasses } from "@mui/x-charts";
import { TrendLine } from "../../../../components/icon/TrendLine";

interface GradientBarChartProps {
  xLabels: string[];
  series: any[];
  methodology: string;
  baseYearEmission: number | null;
  filter: string[];
  tooltip?: (chartsAxisContentProps: ChartsAxisContentProps) => JSX.Element;
  goalItem: GoalItem;
  type?: string;
}

export const GradientBarChart: React.FC<GradientBarChartProps> = ({
  xLabels,
  series,
  methodology,
  baseYearEmission,
  filter,
  tooltip,
  goalItem,
  type,
}) => {
  const showVariance = filter.includes("Base-Year-Reference");
  const shouldScroll = type === "chart" && xLabels.length > 10;

  // console.log("log",series)
  // ✅ ปรับ series ถ้าเป็น baseline และไม่ได้ติ๊ก "Variance Reduction"
  // const adjustedSeries =
  //   methodology === "baseline"
  //     ? series.map((s) =>
  //         s.label === "BAU Emission"
  //           ? {
  //               ...s,
  //               area: showVariance, // ✅ เปิด gradient เฉพาะตอนติ๊ก
  //             }
  //           : s
  //       )
  //     : series;

  const adjustedSeries =
    methodology === "baseline"
      ? series.map((s) =>
          s.label === "Variance Reduction"
            ? {
                ...s,
                area: showVariance, // ✅ เปิด gradient เฉพาะตอนติ๊ก
              }
            : s,
        )
      : series;

  // ✅ ถ้าเป็น baseYear และติ๊กอยู่ → แสดง baseYear reference area
  const baseYearAreaSeries =
    methodology === "baseYear" && baseYearEmission != null && showVariance
      ? [
          {
            type: "line",
            label: "Base-Year-Reference",
            data: xLabels.map(() => baseYearEmission),
            color: "transparent",
            showMark: false,
            area: false,
            areaFill: "url(#grayGradient)",
          },
        ]
      : [];

  return (
    <Box
      sx={{
        width: "100%",
        height: 500, // ✅ ต้องมีความสูงชัดเจน
        backgroundColor: "#fff",
        marginTop: "-25px",
        overflowX: shouldScroll ? "auto" : "hidden", // ✅ เปิด scroll เมื่อจำเป็น
        overflowY: "hidden",
      }}
    >
      <Box
        sx={{
          minWidth: shouldScroll ? `${xLabels.length * 80}px` : "100%",
          height: "100%",
        }}
      >
        {/* ✅ gradient สำหรับเติมสีในพื้นที่ใต้เส้น */}
        <svg width="0" height="0">
          <defs>
            <linearGradient id="greenGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#0B9485" stopOpacity={0.4} />
              <stop offset="40%" stopColor="#0B9485" stopOpacity={0.2} />
              <stop offset="50%" stopColor="#0B9485" stopOpacity={0.05} />
              <stop offset="60%" stopColor="#0B9485" stopOpacity={0} />
            </linearGradient>
          </defs>
        </svg>

        <svg width="0" height="0">
          <defs>
            <linearGradient id="grayGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#0B9485" stopOpacity={0.4} />
              <stop offset="15%" stopColor="#0B9485" stopOpacity={0.2} />
              <stop offset="20%" stopColor="#0B9485" stopOpacity={0.1} />
              <stop offset="25%" stopColor="#0B9485" stopOpacity={0} />
            </linearGradient>
          </defs>
        </svg>

        {/* ✅ Container หลัก */}
        <ResponsiveChartContainer
          series={[...adjustedSeries, ...baseYearAreaSeries]}
          xAxis={[
            {
              scaleType: "band",
              data: xLabels, // @ts-ignore
              // categoryGapRatio: 0,
            },
          ]}
          margin={{ left: 80 }}
          height={400}
          sx={{
            "& .MuiAreaElement-root": {
              fill:
                methodology === "baseline"
                  ? "url(#greenGradient)"
                  : "url(#grayGradient)",
            },
            // ✅ เพิ่ม selector เฉพาะ label "variance reduction"
            // "& path[data-series-label='variance reduction'].MuiAreaElement-root":
            //   {
            //     transform: "translateX(-50%) scaleX(2)", // ขยายเต็ม band
            //     transformOrigin: "center",
            //     shapeRendering: "geometricPrecision",
            //   },

            "& .MuiLineElement-root": { strokeWidth: 3 },
            // "& .MuiMarkElement-root": { r: 4 },
            [`& .${axisClasses.directionY} .${axisClasses.line}`]: {
              display: "none",
            },
            [`& .${axisClasses.directionX} .${axisClasses.line}`]: {
              display: "none",
            },
            [`& .${axisClasses.tick}`]: {
              display: "none",
            },
            [`& .${chartsGridClasses.line}`]: {
              strokeDasharray: "4px 4px",
              stroke: "#E5E6EB",
              strokeWidth: "1px",
            },
            "& .MuiBarElement-root": {
              // style: "strokeWidth:1;stroke:white",
              stroke: "none",
              // opacity: 0.5,
            },
            flexShrink: 0,

            "& .MuiChartsAxis-label": {
              transform: "translateY(-180px)", // เลขยิ่งติดลบมาก ยิ่งขึ้นไปด้านบน
            },
          }}
        >
          {/* ✅ ส่วนการ plot */}
          <AreaPlot />
          <BarPlot />
          <LinePlot />
          <MarkPlot />

          {/* ✅ เส้นแบ่งแนวตั้ง (ระหว่าง column) */}
          {xLabels.slice(1).map((label, index) => (
            <ChartsReferenceLine
              key={index}
              x={label}
              lineStyle={{
                stroke: "#d0d0d0",
                strokeWidth: 1,
                strokeDasharray: "4 4",
                zIndex: 20,
                // ✅ ขยับเส้นไปขอบขวาของ column ก่อนหน้า
                transform: `translateX(${-19 / xLabels.length / 2}%)`,
              }}
            />
          ))}

          {/* ✅ เส้นอ้างอิงแนวนอน */}
          {/* เส้นอ้างอิง baseYear */}
          {methodology === "baseYear" && baseYearEmission != null && (
            <ChartsReferenceLine
              y={baseYearEmission}
              lineStyle={{
                stroke: "#818181",
                strokeWidth: 2,
                strokeDasharray: "5 5",
              }}
            />
          )}
          {methodology === "baseline" && (
            <ChartsReferenceLine
              y={baseYearEmission ?? 0}
              lineStyle={{
                stroke: "#818181",
                strokeWidth: 2,
                strokeDasharray: "5 5",
              }}
            />
          )}

          {/* ✅ แกนและองค์ประกอบอื่น */}
          <ChartsXAxis
            axisLine={false}
            tickLabelStyle={{
              fill: "#818181", // ✅ สีเทา (tailwind gray-400)
              fontSize: 12,
              fontFamily: "Inter, sans-serif",
            }}
          />
          <ChartsYAxis
            axisLine={false} // ❌ ซ่อนเส้นแกน Y
            label="tCO₂e"
            labelStyle={{
              fill: "#818181",
              fontSize: 16,
              fontWeight: 400,
              transform: "rotate(0deg)", // หมุนตัวหนังสือ
            }}
            tickLabelStyle={{
              fill: "#818181", // ✅ สีเทา
              fontSize: 12,
              fontFamily: "Inter, sans-serif",
            }}
          />
          <ChartsTooltip
            slots={{
              axisContent: (p) => (
                <CustomGoalTooltip
                  {...p}
                  methodology={methodology}
                  baseYearEmission={baseYearEmission}
                  goal={goalItem}
                />
              ),
            }}
            trigger="axis"
          />
        </ResponsiveChartContainer>

        <Box
          display="flex"
          alignItems="center"
          sx={{
            marginX: "50px",
            marginLeft: "80px",
          }}
        >
          {xLabels.map((label, idx) => {
            const plan = goalItem?.plans?.find(
              (p: any) => Number(p.year) === Number(label),
            );
            const totalValue =
              plan && typeof plan.total === "number" ? plan.total : null;
            const isZero = totalValue === 0 || totalValue === null;

            return (
              <Box
                key={idx}
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                sx={{
                  flex: 1, // ✅ ช่องละเท่ากันกับ tick แต่ละปี
                  gap: "4px",
                }}
              >
                {isZero ? (
                  <img
                    src="/img/stable.png"
                    alt="minus-total"
                    width="20px"
                    height="20px"
                  />
                ) : (
                  <img
                    src="/img/Vector.svg"
                    alt="plus-total"
                    width="20px"
                    height="20px"
                  />
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

interface CustomGoalTooltipProps extends ChartsAxisContentProps {
  methodology?: string;
  baseYearEmission?: number | null;
  goal: GoalItem;
}

const CustomGoalTooltip = ({
  axisData,
  series,
  goal,
  methodology,
}: CustomGoalTooltipProps) => {
  const { t } = useTranslation([common], { nsMode: "fallback" });
  if (!axisData) return null;

  const year = Number(axisData?.x?.value ?? axisData?.y?.value ?? 0);
  if (!year || Number.isNaN(year)) return null;

  let plan: GoalDashboardBaseYear | GoalDashboardBaseline | undefined;
  // console.log("log", goal);

  if (methodology === "baseYear") {
    plan = (goal.plans as GoalDashboardBaseYear[]).find(
      (p) => Number(p.year) === year,
    );
  } else if (methodology === "baseline") {
    plan = (goal.plans as GoalDashboardBaseline[]).find(
      (p) => Number(p.year) === year,
    );
  }

  // 🛡️ Prevent error when plan not found
  if (!plan) return null;

  let baseYearEmission: number | null = null;
  let bauEmission: number | null = null;
  let goalEmission: number | null = null;
  let scope1: number | null = null;
  let scope2: number | null = null;
  let scope3: number | null = null;
  let totalEmission: number | null = null;
  let targetVariance: number | null = null;
  let reductionVariance: number | null = null;
  let percentReductionVariance: number | null = null;

  if (methodology === "baseYear") {
    const p = plan as GoalDashboardBaseYear;
    baseYearEmission = p.baseYearEmission ?? null;
    goalEmission = p.targetEmission ?? null;
    scope1 = p.scope1 ?? null;
    scope2 = p.scope2 ?? null;
    scope3 = p.scope3 ?? null;
    totalEmission = p.total ?? null;
    targetVariance = p.targetVariance ?? null;
    reductionVariance = p.reductionVariance ?? null;
    percentReductionVariance = p.reductionVariancePercent ?? null;
  } else {
    const p = plan as GoalDashboardBaseline;
    baseYearEmission = p.baseYearReduction ?? 0;
    bauEmission = p.bauEmission ?? null;
    goalEmission = p.targetEmission ?? null;
    scope1 = p.scope1 ?? null;
    scope2 = p.scope2 ?? null;
    scope3 = p.scope3 ?? null;
    totalEmission = p.total ?? null;
    targetVariance = p.targetVariance ?? null;
    reductionVariance = p.reductionVariance ?? null;
    percentReductionVariance = p.reductionVariancePercent ?? null;
  }

  // console.log("show",percentReductionVariance, methodology)
  const renderRow = (
    label: string,
    colorOrBorder: string,
    value: number | null,
    indicatorType: "line" | "box" | "none" = "line",
    dashed?: boolean,
    percent?: number | null,
  ) => {
    if (value == null) return null;

    // ✅ Indicator
    let indicator: JSX.Element | null = null;
    if (indicatorType === "box") {
      indicator = (
        <Box
          sx={{
            width: 14,
            height: 14,
            borderRadius: "4px",
            backgroundColor: colorOrBorder,
            border:
              colorOrBorder === "#FFFFFF" ? "1px solid #E5E7EB" : undefined,
            flexShrink: 0,
          }}
        />
      );
    } else if (indicatorType === "line") {
      let IconComponent: React.ReactNode | null = null;
      let iconColor = colorOrBorder;
      switch (label) {
        case t("Base-Year-Emission"):
          IconComponent = (
            <img src="/img/Line1.svg" style={{ width: "14px" }} />
          );
          iconColor = "#818181";
          break;

        case t("Bau-Emission"):
          IconComponent = <TrendLine sx={{ width: 14 }} />;
          iconColor = "#FF4747";
          break;

        case t("Goal"):
          IconComponent = <TrendLine sx={{ width: 14 }} />;
          iconColor = "#0B9585";
          break;
        // ✅ ไม่ต้องมี default → ไม่มี icon
        default:
          IconComponent = null;
          break;
      }

      indicator = (
        <Box
          sx={{
            color: colorOrBorder,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {IconComponent}
        </Box>
      );
    } else {
      indicator = <Box sx={{ width: 16, height: 0, flexShrink: 0 }} />;
    }

    // ✅ Logic สีของค่า (เฉพาะ Target-Variance เท่านั้น)
    const isTargetVariance = label === t("Target-Variance");
    const isReductionVariance = label === t("Reduction-Variance");

    let displayValue = NumberFormatterUtils.numberFormat(value ?? 0);
    let valueColor = "var(--dark)";

    if (isTargetVariance) {
      if (value > 0) {
        displayValue = `+${displayValue}`;
        valueColor = "var(--red)";
      } else if (value < 0) {
        valueColor = "var(--green)";
      }
    }

    // ✅ สีของเปอร์เซ็นต์ (เฉพาะ Reduction-Variance)
    const percentColor =
    isReductionVariance && percent != null
      ? percent > 0
        ? "var(--green)"
        : "var(--red)"
      : "var(--dark)";

    return (
      <>
        <Box display="flex" alignItems="center" gap="6px">
          {indicator}

          <Typography variant="body1" color="var(--dark)" flex={2}>
            {label}
          </Typography>

          <Box
            flex={3}
            display="grid"
            gridTemplateColumns="minmax(80px,auto) 55px 80px"
            justifyContent="end"
            alignItems="baseline"
            columnGap="4px"
            sx={{ whiteSpace: "nowrap" }}
          >
            {/* 🔹 ค่า */}
            <Typography variant="body1" color={valueColor} textAlign="right">
              {displayValue}
            </Typography>

            {/* 🔹 หน่วย */}
            <Typography variant="body1" color="var(--dark)" textAlign="left">
              tCO<sub>2</sub>e
            </Typography>

            {/* 🔹 เปอร์เซ็นต์ */}
            <Typography variant="body1" color={percentColor} textAlign="center">
              {isReductionVariance && percent != null
                ? `${NumberFormatterUtils.numberFormat(percent)}%`
                : ""}
            </Typography>
          </Box>
        </Box>
        <Divider />
      </>
    );
  };

  return (
    <Paper
      elevation={4}
      sx={{
        backgroundColor: "#fff",
        borderRadius: 2,
        border: "1px solid #E5E7EB",
        p: 1.5,
        minWidth: 425,
        maxWidth: 425,
        boxShadow: "0px 2px 8px rgba(0,0,0,0.12)",
        fontFamily: "Inter, sans-serif",
      }}
    >
      {/* Year header */}
      <Typography variant="h3" color="var(--dark)" my={1}>
        {year}
      </Typography>
      <Divider />

      {/* BaseYear / BAU */}
      {renderRow(
        t("Base-Year-Emission"),
        "#818181",
        baseYearEmission,
        "line",
        true,
      )}
      {renderRow(t("Bau-Emission"), "#FF4747", bauEmission, "line")}

      {/* Plan header */}
      <Typography variant="h3" color="var(--dark)" my={1}>
        {t("Plan")}
      </Typography>
      <Divider />
      {renderRow(t("Goal"), "#0B9585", goalEmission, "line")}

      {/* Actuality header */}
      <Typography variant="h3" color="var(--dark)" my={1}>
        {t("Actuality")}
      </Typography>
      <Divider />

      {renderRow(t("Scope-1"), "#2563EB", scope1, "box")}
      {renderRow(t("Scope-2"), "#EC4899", scope2, "box")}
      {renderRow(t("Scope-3"), "#F59E0B", scope3, "box")}
      {renderRow(t("Total-Emissions"), "#FFFFFF", totalEmission, "none")}
      {renderRow(t("Target-Variance"), "#FFFFFF", targetVariance, "none")}
      {renderRow(
        t("Reduction-Variance"),
        "#93D2CA",
        reductionVariance,
        "box",
        false,
        percentReductionVariance,
      )}
    </Paper>
  );
};
